import { useCallback, useEffect, useState } from 'react';
import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import { GigReward, ParsedPromo, ParsedXPPromo, UserGigQuery, XPPromo } from '../../components/Layout/Promotions/types';
import { useUserContext } from '../../providers/UserProvider';
import { xTenantId } from '../../consts';
import { Endpoint } from '../../models/api.model';
import {
  useTagsObjectLazyQuery,
  TagsObjectQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import { PlatformPromos, PromoTagCategory, UserTags } from './types';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { loadPlatformPromotions } from '../../state/promotions';
import { setLoadPlatformPromotions } from '../../state/promotions/reducers';
import { claimXPPromo, getXPPromos } from '../../components/Layout/Promotions/services';
import {
  UserFreeTicketQuery,
  useUserFreeTicketLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-ticket-get.generated';
import {
  UserFreeSpinQuery,
  useUserFreeSpinLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-spin-get.generated';
import { useLocation } from 'react-router-dom';
import { getActualXPToken } from '../../utils/auth';

const STARTED_STATUS = 'started';
const COMPLETED_STATUS = 'completed';
const CURRENCY_BASED_EVENTS = ['enriched game', 'deposit', 'enriched bingo'];

export const useFetchPlatformPromos = (): PlatformPromos => {
  const { userProfileId, XPToken, setXPToken } = useUserContext();
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const dispatch = useDispatch();
  const location = useLocation();
  const loadPlatformPromotionsState = useSelector(loadPlatformPromotions);

  const [isPromosLoading, setIsPromotionsLoading] = useState<boolean>(true);
  const [userTags, setUserTags] = useState<Nullable<UserTags>>(null);
  const [plPromos, setPlPromos] = useState<ParsedXPPromo[]>([]);
  const [rewardsPromos, setRewardsPromos] = useState<ParsedPromo[]>([]);
  const [XPPromosError, setXPPromosError] = useState<unknown>('');
  const [isAlreadyFetched, setIsAlreadyFetched] = useState<boolean>(false);

  const onQueryError = (e: ApolloError): void => console.error(e.message);

  const handleRewards = useCallback((data: UserGigQuery): void => {
    const gigFreeTicket = (data as UserFreeTicketQuery).userFreeTicket?.data;
    const gigFreeSpins = (data as UserFreeSpinQuery).userFreeSpin?.data;

    const rewards = gigFreeTicket || gigFreeSpins || [];

    const receivedRewards: ParsedPromo[] = (rewards as GigReward[])
      .map((reward: GigReward) => {
        const resPromoId = reward?.freeSpinsTemplateId || reward?.freeTicketsTemplateId;

        return resPromoId
          ? {
              id: reward.id,
              platformId: resPromoId,
              status: reward.status,
            }
          : null;
      })
      .filter(Boolean) as ParsedPromo[];

    setRewardsPromos((prev) => [...prev, ...receivedRewards]);
  }, []);

  const handleUserTags = (userTagsData: TagsObjectQuery): void => {
    setUserTags(
      userTagsData.tagsObject
        ?.filter((item) => item.tag?.tagCategory?.name === PromoTagCategory.BONUS)
        .map((item) => ({ tagId: item.tag?.id, tagName: item.tag?.name }))
    );
  };

  const [fetchUserTags, { error: fetchUserTagsError }] = useTagsObjectLazyQuery({
    variables: {
      xTenantId,
      externalResource: 'UserProfileId',
      externalResourceId: userProfileId,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handleUserTags,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  });

  const fetchRewards = {
    variables: {
      xTenantId,
      userProfileId,
      pageLimit: 1000,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handleRewards,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  };

  const [fetchBonusesFreeSpinGig, { error: fetchBonusesFreeSpinGigError }] = useUserFreeSpinLazyQuery(fetchRewards);

  const [fetchBonusesFreeTicketGig, { error: fetchBonusesFreeTicketGigError }] =
    useUserFreeTicketLazyQuery(fetchRewards);

  const promosLoadingError =
    fetchUserTagsError || XPPromosError || fetchBonusesFreeSpinGigError || fetchBonusesFreeTicketGigError;

  const claimCompletedPromo = async (XPPromo: XPPromo): Promise<void> => {
    if (XPPromo.status === COMPLETED_STATUS) {
      await claimXPPromo(XPPromo.quest.id, XPToken);
      dispatch(setLoadPlatformPromotions(true));
    }
  };

  const parseXPPromos = (XPPromos: XPPromo[]): ParsedXPPromo[] =>
    XPPromos.map((XPPromo) => {
      const questChallenges = XPPromo.quest.challenges;

      const targetChallenge: {
        index: number;
        challenge: (typeof questChallenges)[0];
      } = { index: 0, challenge: questChallenges[0] };

      XPPromo.quest.challenges.some((challenge, index) => {
        const isTarget = CURRENCY_BASED_EVENTS.includes(challenge.event.toLowerCase());

        if (isTarget) {
          targetChallenge.index = index;
          targetChallenge.challenge = challenge;
        }

        return isTarget;
      });

      claimCompletedPromo(XPPromo);

      const targetEvent = targetChallenge.challenge?.event?.toLowerCase() ?? '';

      const target = Number(targetChallenge.challenge?.target);
      const progress = Math.min(Number(XPPromo.challenges[targetChallenge.index]?.progress), target);

      return {
        platformId: XPPromo.quest.id,
        isActive: XPPromo.status === STARTED_STATUS,
        progress,
        target,
        isCurrencyBased: CURRENCY_BASED_EVENTS.includes(targetEvent),
      };
    });

  useEffect(() => {
    if (isAuthenticated) {
      (async (): Promise<void> => {
        !isAlreadyFetched &&
          (await Promise.all([fetchUserTags(), fetchBonusesFreeSpinGig(), fetchBonusesFreeTicketGig()]));

        setIsAlreadyFetched(true);
      })();
    }
  }, [isAuthenticated, user?.access_token]);

  useEffect(() => {
    let cancelled = false;

    if (XPToken && loadPlatformPromotionsState) {
      (async (): Promise<void> => {
        try {
          const activeToken = await getActualXPToken(XPToken, setXPToken);
          const receivedXPPromos = await getXPPromos(activeToken);

          if (cancelled) return;

          setPlPromos(parseXPPromos(receivedXPPromos));
        } catch (error) {
          if (cancelled) return;

          setXPPromosError(error);
        } finally {
          if (!cancelled) {
            setIsPromotionsLoading(false);
            dispatch(setLoadPlatformPromotions(false));
          }
        }
      })();
    }

    return (): void => {
      cancelled = true;
    };
  }, [loadPlatformPromotionsState, XPToken]);

  useEffect(() => {
    // Trigger platform promotions fetching if route is changed to promotion page and vice versa
    dispatch(setLoadPlatformPromotions(true));
  }, [location]);

  useEffect(() => {
    // Remove spinner for logged out state
    !(isAuthLoading || isAuthenticated) && setIsPromotionsLoading(false);
  }, [isAuthLoading, isAuthenticated]);

  return {
    platformPromos: {
      plPromos,
      rewardsPromos,
      userTags,
    },
    isPromosLoading,
    promosLoadingError,
  };
};
